import Image from 'next/image'
import {FC, useEffect, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextVariant} from '@/components/common/content/Text'
import {SkeletonBlock} from '@/components/common/skeleton/SkeletonBlock'
import {usePreferences} from '@/components/context/Preferences'
import {useHeaderContent} from '@/content/header'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {domainLabels} from '@/utils/countries'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {ChevronIcon} from '../common/icons/ChevronIcon'
import {Modal} from '../common/modal/Modal'
import {DomainSelectLazy} from '../inputs/DomainSelectLazy'

const DOMAIN_ICON_SIZE = 20
// Roughly the widest domain label, so the chevron does not jump when it resolves.
const DOMAIN_LABEL_PLACEHOLDER_WIDTH = '8ch'

export const DomainSettings: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation('page')
  const {domainTitle, saveButtonText} = useHeaderContent().regionalSettings
  const {domain, setDomain, isPreferencesPending} = usePreferences()
  const [isModalActive, setIsModalActive] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState(domain)

  // On a statically rendered page `domain` starts as the default and only becomes real once the
  // client resolves preferences, so the select would otherwise open pre-set to the wrong domain.
  useEffect(() => {
    setSelectedDomain(domain)
  }, [domain])

  const saveSettings = async () => {
    setIsModalActive(false)
    setDomain(selectedDomain)
  }

  const domainIcon = domainLabels[domain].icon
  const domainLabel = domainLabels[domain].label

  return (
    <>
      <button className="wrapper" type="button" onClick={() => setIsModalActive(true)}>
        {/* The domain comes from the request host, which a statically rendered document does not
            have — one prerender is served to every TLD. Hold the space until the client resolves
            it rather than showing .com to a .cz visitor and swapping it a moment later. */}
        <div className="icon">
          {isPreferencesPending ? (
            <SkeletonBlock width={DOMAIN_ICON_SIZE} height={DOMAIN_ICON_SIZE} borderRadius="50%" />
          ) : (
            <Image
              src={domainIcon}
              alt="domain"
              style={{objectFit: 'contain'}}
              width={20}
              height={20}
            />
          )}
        </div>
        {isPreferencesPending ? (
          <SkeletonBlock width={DOMAIN_LABEL_PLACEHOLDER_WIDTH} height="0.9em" />
        ) : (
          <Text variant={TextVariant.text14} color={theme.palette.text.white} element="span">
            {domainLabel}
          </Text>
        )}
        <div className="chevron-wrapper">
          <ChevronIcon color={theme.palette.text.white} isActive={isModalActive} />
        </div>
      </button>
      <Modal
        isVisible={isModalActive}
        closeModal={() => setIsModalActive(false)}
        hasPadding={false}
      >
        <div className="content">
          <Text variant={TextVariant.h5} element="h2">
            {t(domainTitle)}
          </Text>
          <ul className="list">
            <li className="item">
              <DomainSelectLazy {...{selectedDomain, setSelectedDomain}} />
            </li>
          </ul>
          <div className="button-wrapper">
            <Button
              variant={ButtonVariant.primary}
              type="button"
              size={ButtonSize.small}
              fullWidth
              onClick={saveSettings}
            >
              {t(saveButtonText)}
            </Button>
          </div>
        </div>
      </Modal>

      <style jsx>{`
        .wrapper {
          display: flex;
          align-items: center;
          padding: ${theme.spacing[1]};
          margin: -${theme.spacing[1]};
        }

        .icon {
          position: relative;
          width: 20px;
          height: 20px;
          flex: 0 0 20px;
          margin-right: ${theme.spacing[2]};
          border-radius: 50%;
        }

        .chevron-wrapper {
          margin-left: ${theme.spacing[2]};
        }

        .content {
          min-width: 370px;
          height: 100%;
          padding: ${theme.spacing[5]};
        }

        .list {
          margin-top: ${theme.spacing[3]};
        }

        .button-wrapper {
          margin: ${theme.spacing[3]} auto 0;
          max-width: 130px;
        }

        @media ${MOBILE_SCREENSIZE} {
          .content {
            min-width: inherit;
            padding: ${theme.spacing[4]};
          }

          .list {
            margin-top: ${theme.spacing[2]};
          }
        }
      `}</style>
    </>
  )
}
